/**
 * Full-draft board geometry and roster-based team needs.
 *
 * The board is intentionally a transparent view of the draft rather than a
 * second recommendation engine. Pick cells come from the live draft, and the
 * need badges come from each team's current roster and league slots. They say
 * "this team still has an open WR slot"; they do not claim to know what a
 * manager will do with that information.
 */

import type { DraftPick, LeagueConfig, PlayerId, Position } from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';
import { computeRosterNeed } from './valuation.ts';

export interface DraftBoardTeamInput {
  roster_id: number;
  draft_slot: number;
  team_name: string;
  player_ids: PlayerId[];
  pick_count: number;
}

export interface DraftTeamNeed {
  position: Position;
  open_starters: number;
  score: number;
}

export interface DraftBoardTeam {
  roster_id: number;
  draft_slot: number;
  team_name: string;
  player_ids: PlayerId[];
  pick_count: number;
  needs: DraftTeamNeed[];
  open_flex: number;
  roster_size: number;
}

export interface DraftBoardCell {
  round: number;
  draft_slot: number;
  pick_no: number;
  pick: DraftPick | null;
}

/** The 1-based draft slot selecting at a given overall pick number. */
export function draftSlotForPick(pickNo: number, teams: number, draftType: string): number {
  if (teams <= 0 || pickNo <= 0) return 1;
  const round = Math.ceil(pickNo / teams);
  const indexInRound = ((pickNo - 1) % teams) + 1;
  if (draftType === 'linear') return indexInRound;
  return round % 2 === 0 ? teams - indexInRound + 1 : indexInRound;
}

/** The overall pick number for a round/slot cell in a snake or linear draft. */
export function pickNumberForSlot(
  round: number,
  draftSlot: number,
  teams: number,
  draftType: string,
): number {
  if (round <= 0 || draftSlot <= 0 || teams <= 0) return 0;
  const indexInRound = draftType === 'linear' ? draftSlot : round % 2 === 0 ? teams - draftSlot + 1 : draftSlot;
  return (round - 1) * teams + indexInRound;
}

export function buildDraftBoardCells(
  rounds: number,
  teams: number,
  draftType: string,
  picks: DraftPick[],
): DraftBoardCell[][] {
  const byPick = new Map(picks.map((pick) => [pick.pick_no, pick]));
  const out: DraftBoardCell[][] = [];

  for (let round = 1; round <= rounds; round++) {
    const row: DraftBoardCell[] = [];
    for (let draftSlot = 1; draftSlot <= teams; draftSlot++) {
      const pick_no = pickNumberForSlot(round, draftSlot, teams, draftType);
      row.push({ round, draft_slot: draftSlot, pick_no, pick: byPick.get(pick_no) ?? null });
    }
    out.push(row);
  }

  return out;
}

export function buildDraftBoardTeams(
  league: LeagueConfig,
  teams: DraftBoardTeamInput[],
  positionOf: (id: PlayerId) => Position | null,
): DraftBoardTeam[] {
  return teams.map((team) => {
    const need = computeRosterNeed(team.player_ids, league, positionOf);
    const needs = FANTASY_POSITIONS
      .filter((position) => (need.open_starters[position] ?? 0) > 0)
      .map((position) => ({
        position,
        open_starters: need.open_starters[position] ?? 0,
        score: need.score[position] ?? 0,
      }))
      .sort((a, b) => b.score - a.score || b.open_starters - a.open_starters);

    return {
      roster_id: team.roster_id,
      draft_slot: team.draft_slot,
      team_name: team.team_name,
      player_ids: [...team.player_ids],
      pick_count: team.pick_count,
      needs,
      open_flex: need.open_flex.reduce((total, flex) => total + flex.count, 0),
      roster_size: team.player_ids.length,
    };
  });
}

